import { useEffect, useMemo, useState } from "react";
import {
  Star, Mail, Trash2, ChevronDown, Loader2, Save, Filter, Send, Settings2, RefreshCw, Eye, EyeOff, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { PageHeader, SectionCard, FullPageLoader } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const RATING_FILTERS = [
  { value: "", label: "All ratings" },
  { value: "5", label: "5★ only" },
  { value: "4", label: "4★ only" },
  { value: "3", label: "3★ only" },
  { value: "2", label: "2★ only" },
  { value: "1", label: "1★ only" },
];

const PUB_FILTERS = [
  { value: "all", label: "All" },
  { value: "published", label: "Published" },
  { value: "unpublished", label: "Unpublished" },
];

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pubFilter, setPubFilter] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("");
  const [expanded, setExpanded] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(false);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = {};
      if (pubFilter === "published") params.published = true;
      if (pubFilter === "unpublished") params.published = false;
      if (ratingFilter) params.rating = Number(ratingFilter);
      const { data } = await apiClient.get("/admin/reviews", { params });
      setReviews(data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load reviews"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, [pubFilter, ratingFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePublish = async (review) => {
    try {
      await apiClient.patch(`/admin/reviews/${review.id}/publish`, { published: !review.published });
      setReviews((rs) => rs.map((r) => (r.id === review.id ? { ...r, published: !review.published } : r)));
      toast.success(!review.published ? "Published" : "Unpublished");
    } catch (err) {
      toast.error(getErrorMessage(err, "Update failed"));
    }
  };

  const sendGoogleInvite = async (review) => {
    try {
      const { data } = await apiClient.post(`/admin/reviews/${review.id}/send-google-invite`);
      setReviews((rs) => rs.map((r) => (r.id === review.id ? { ...r, google_review_email_sent_at: data.sent_at, google_review_email_count: data.count } : r)));
      toast.success("Google review invite sent");
    } catch (err) {
      const msg = getErrorMessage(err, "Send failed");
      if (/google review url/i.test(msg)) {
        toast.error(msg, {
          action: {
            label: "Open Settings",
            onClick: () => setSettingsOpen(true),
          },
          duration: 8000,
        });
      } else {
        toast.error(msg);
      }
    }
  };

  const deleteReview = async (review) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await apiClient.delete(`/admin/reviews/${review.id}`);
      setReviews((rs) => rs.filter((r) => r.id !== review.id));
      toast.success("Review deleted");
    } catch (err) {
      toast.error(getErrorMessage(err, "Delete failed"));
    }
  };

  const runReminders = async () => {
    setReminderBusy(true);
    try {
      const { data } = await apiClient.post("/admin/reviews/process-reminders");
      toast.success(`Reminder run: sent ${data.sent}, checked ${data.checked}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Reminder run failed"));
    } finally {
      setReminderBusy(false);
    }
  };

  const stats = useMemo(() => {
    if (reviews.length === 0) return { avg: 0, count: 0, published: 0 };
    const sum = reviews.reduce((a, r) => a + r.rating, 0);
    return {
      avg: (sum / reviews.length).toFixed(2),
      count: reviews.length,
      published: reviews.filter((r) => r.published).length,
    };
  }, [reviews]);

  return (
    <div>
      <PageHeader title="Reviews" subtitle="Moderate visitor reviews, send Google invites, and configure settings.">
        <div className="flex gap-2">
          <button onClick={runReminders} disabled={reminderBusy} className="neon-outline-btn px-4 py-2" data-testid="run-reminders">
            {reminderBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Run reminders
          </button>
          <button onClick={() => setSettingsOpen(true)} className="neon-btn px-4 py-2" data-testid="open-review-settings">
            <Settings2 className="w-4 h-4" /> Settings
          </button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard label="Loaded" value={stats.count} />
        <StatCard label="Published" value={stats.published} />
        <StatCard label="Average" value={stats.avg + " ★"} />
      </div>

      <SectionCard className="mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="w-4 h-4 text-slate-400" />
          <select value={pubFilter} onChange={(e) => setPubFilter(e.target.value)} className="input-cyber py-2 w-auto" data-testid="pub-filter">
            {PUB_FILTERS.map((o) => <option key={o.value} value={o.value} className="bg-slate-900">{o.label}</option>)}
          </select>
          <select value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)} className="input-cyber py-2 w-auto" data-testid="rating-filter">
            {RATING_FILTERS.map((o) => <option key={o.value} value={o.value} className="bg-slate-900">{o.label}</option>)}
          </select>
          <button onClick={fetchReviews} className="neon-outline-btn px-3 py-2 ml-auto" data-testid="refresh-reviews">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </SectionCard>

      {loading ? (
        <FullPageLoader label="Loading reviews" />
      ) : reviews.length === 0 ? (
        <SectionCard>
          <div className="text-slate-400 text-center py-8 flex flex-col items-center gap-3">
            <MessageSquare className="w-8 h-8 text-slate-600" /> No reviews match this filter.
          </div>
        </SectionCard>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <ReviewRow
              key={r.id}
              review={r}
              expanded={!!expanded[r.id]}
              onToggleExpand={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}
              onTogglePublish={() => togglePublish(r)}
              onSendInvite={() => sendGoogleInvite(r)}
              onDelete={() => deleteReview(r)}
            />
          ))}
        </div>
      )}

      {settingsOpen && (
        <ReviewSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}

const StatCard = ({ label, value }) => (
  <SectionCard>
    <p className="text-slate-500 text-xs tracking-widest uppercase">{label}</p>
    <p className="font-display font-black text-white text-2xl mt-1">{value}</p>
  </SectionCard>
);

const ReviewRow = ({ review, expanded, onToggleExpand, onTogglePublish, onSendInvite, onDelete }) => {
  const authorName = `${review.user.first_name || ""} ${review.user.last_name || ""}`.trim() || review.user.email || "Unknown";
  const inviteInfo = review.google_review_email_count
    ? `Sent ${review.google_review_email_count}× · ${format(parseISO(review.google_review_email_sent_at), "PPp")}`
    : "Never sent";
  return (
    <div className="rounded-2xl border p-5" style={{ background: "rgba(14,18,34,0.72)", borderColor: "rgba(120,150,220,0.14)" }} data-testid={`review-row-${review.id}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-4 h-4" strokeWidth={1.5}
                  style={{ color: s <= review.rating ? "#ffb020" : "#334155", fill: s <= review.rating ? "#ffb020" : "transparent" }} />
              ))}
            </div>
            <span className="text-white font-display font-bold">{review.rating}★</span>
            <span
              className="chip"
              style={{
                background: review.published ? "rgba(0,255,157,0.14)" : "rgba(148,163,184,0.14)",
                border: `1px solid ${review.published ? "#00ff9d" : "#94a3b8"}`,
                color: review.published ? "#00ff9d" : "#cbd5e1",
              }}
            >
              {review.published ? "PUBLISHED" : "UNPUBLISHED"}
            </span>
          </div>
          {review.title && <p className="text-white font-display font-bold mt-2">{review.title}</p>}
          <p className={`text-slate-300 text-sm mt-2 whitespace-pre-wrap ${expanded ? "" : "line-clamp-3"}`}>{review.body}</p>
          {review.body.length > 220 && (
            <button onClick={onToggleExpand} className="text-[var(--neon-cyan)] text-xs font-bold mt-2 flex items-center gap-1" data-testid={`expand-${review.id}`}>
              <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
              {expanded ? "Collapse" : "Read more"}
            </button>
          )}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-slate-500 text-xs">
            <span>{authorName}</span>
            <span>·</span>
            <span>{review.user.email}</span>
            <span>·</span>
            <span>{format(parseISO(review.created_at), "PPp")}</span>
            {review.booking?.stations?.length ? (<><span>·</span><span>{review.booking.stations.join(", ")}</span></>) : null}
          </div>
          <p className="text-slate-600 text-xs mt-1">Google invite: {inviteInfo}</p>
        </div>

        <div className="flex flex-row md:flex-col gap-2 shrink-0">
          <button onClick={onTogglePublish}
            className={review.published ? "neon-outline-btn px-3 py-2 text-xs" : "neon-btn px-3 py-2 text-xs"}
            data-testid={`toggle-publish-${review.id}`}>
            {review.published ? <><EyeOff className="w-3.5 h-3.5" /> Unpublish</> : <><Eye className="w-3.5 h-3.5" /> Publish</>}
          </button>
          <button onClick={onSendInvite} className="neon-outline-btn px-3 py-2 text-xs" data-testid={`invite-${review.id}`}>
            <Send className="w-3.5 h-3.5" /> Google invite
          </button>
          <button onClick={onDelete} className="px-3 py-2 rounded-xl border text-xs font-bold flex items-center justify-center gap-1"
            style={{ borderColor: "rgba(239,68,68,0.4)", color: "#fca5a5", background: "rgba(239,68,68,0.08)" }}
            data-testid={`delete-${review.id}`}>
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

function ReviewSettingsDialog({ open, onClose }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    apiClient.get("/admin/review-settings").then(({ data }) => setSettings(data)).catch((e) => toast.error(getErrorMessage(e, "Load failed")));
  }, [open]);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const payload = { ...settings };
      if (typeof payload.reminder_cadence_days === "string") {
        payload.reminder_cadence_days = payload.reminder_cadence_days.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => Number.isFinite(n) && n > 0);
      }
      const { data } = await apiClient.put("/admin/review-settings", payload);
      setSettings(data);
      toast.success("Settings saved");
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f1115] border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="review-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-widest">REVIEW SETTINGS</DialogTitle>
          <DialogDescription>Configure points, reminder cadence, Google review URL, and email templates.</DialogDescription>
        </DialogHeader>

        {!settings ? (
          <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-white text-xs font-bold tracking-widest mb-1.5 block">Points per review</label>
                <input type="number" min="0" value={settings.points_per_review} onChange={(e) => update({ points_per_review: parseInt(e.target.value || "0", 10) })} className="input-cyber" data-testid="setting-points" />
              </div>
              <div>
                <label className="text-white text-xs font-bold tracking-widest mb-1.5 block">Edit window (days)</label>
                <input type="number" min="0" max="90" value={settings.edit_window_days} onChange={(e) => update({ edit_window_days: parseInt(e.target.value || "0", 10) })} className="input-cyber" data-testid="setting-edit-window" />
              </div>
              <div>
                <label className="text-white text-xs font-bold tracking-widest mb-1.5 block">Max reminder emails</label>
                <input type="number" min="0" max="8" value={settings.max_reminders} onChange={(e) => update({ max_reminders: parseInt(e.target.value || "0", 10) })} className="input-cyber" data-testid="setting-max-reminders" />
              </div>
              <div>
                <label className="text-white text-xs font-bold tracking-widest mb-1.5 block">Auto Google invite ≥ rating (0 to disable)</label>
                <input type="number" min="0" max="5" value={settings.auto_google_invite_min_rating} onChange={(e) => update({ auto_google_invite_min_rating: parseInt(e.target.value || "0", 10) })} className="input-cyber" data-testid="setting-auto-min" />
              </div>
            </div>

            <div>
              <label className="text-white text-xs font-bold tracking-widest mb-1.5 block">Reminder cadence (days after visit, comma-separated)</label>
              <input
                type="text"
                value={Array.isArray(settings.reminder_cadence_days) ? settings.reminder_cadence_days.join(", ") : settings.reminder_cadence_days}
                onChange={(e) => update({ reminder_cadence_days: e.target.value })}
                className="input-cyber"
                placeholder="1, 3, 7, 14"
                data-testid="setting-cadence"
              />
            </div>

            <div>
              <label className="text-white text-xs font-bold tracking-widest mb-1.5 block">Google review URL</label>
              <input type="url" value={settings.google_review_url} onChange={(e) => update({ google_review_url: e.target.value })} className="input-cyber" placeholder="https://g.page/r/..." data-testid="setting-google-url" />
            </div>

            <div className="border-t border-slate-800 pt-4">
              <h4 className="text-white font-display font-bold uppercase tracking-widest text-xs mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Reminder email
              </h4>
              <label className="text-slate-400 text-xs mb-1 block">Subject</label>
              <input type="text" value={settings.reminder_email_subject} onChange={(e) => update({ reminder_email_subject: e.target.value })} className="input-cyber mb-3" data-testid="setting-reminder-subject" />
              <label className="text-slate-400 text-xs mb-1 block">Body — tokens: {"{first_name} {visit_date} {review_url} {points}"}</label>
              <textarea rows={6} value={settings.reminder_email_body} onChange={(e) => update({ reminder_email_body: e.target.value })} className="input-cyber resize-none" data-testid="setting-reminder-body" />
            </div>

            <div className="border-t border-slate-800 pt-4">
              <h4 className="text-white font-display font-bold uppercase tracking-widest text-xs mb-3 flex items-center gap-2">
                <Send className="w-4 h-4" /> Google invite email
              </h4>
              <label className="text-slate-400 text-xs mb-1 block">Subject</label>
              <input type="text" value={settings.google_invite_subject} onChange={(e) => update({ google_invite_subject: e.target.value })} className="input-cyber mb-3" data-testid="setting-invite-subject" />
              <label className="text-slate-400 text-xs mb-1 block">Body — tokens: {"{first_name} {rating} {google_url} {business_name}"}</label>
              <textarea rows={6} value={settings.google_invite_body} onChange={(e) => update({ google_invite_body: e.target.value })} className="input-cyber resize-none" data-testid="setting-invite-body" />
            </div>
          </div>
        )}

        <DialogFooter>
          <button onClick={onClose} className="neon-outline-btn px-5 py-2.5">Close</button>
          <button onClick={save} disabled={saving || !settings} className="neon-btn px-6 py-2.5" data-testid="save-review-settings">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
